import useAuthStore from '@/store/useAuthStore'
import { IResponseType } from '@/types/response-type'
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import { toast } from 'react-toastify'
import { collectApiErrorMessages } from '@/utils/functions/collectApiErrorMessages'

const BASE_URL = import.meta.env.VITE_PUBLIC_API_BASEURL
const TIMEOUT = 100_000
const OTP_ENDPOINTS = ['user/login/']

const requestInterceptor = async (config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore.getState()
  const isOtp = OTP_ENDPOINTS.includes(config.url ?? '')

  if (accessToken && !isOtp) {
    config.headers.set('Authorization', `${accessToken}`)
  }

  return config
}

const showErrorToast = (message: string) => {
  toast.error(message)
}

const responseInterceptor = async (error: AxiosError<IResponseType<{}>>) => {
  const { setTokens } = useAuthStore.getState()
  const { response, code } = error

  if (!response) {
    if (code === 'ECONNABORTED') {
      showErrorToast('زمان درخواست به پایان رسید. دوباره تلاش کنید.')
    } else {
      showErrorToast('ارتباط با سرور برقرار نشد.')
    }

    return Promise.reject(error)
  }

  const { status, data } = response

  if ([401, 403].includes(status)) {
    showErrorToast('لطفا دوباره وارد شوید')
    setTokens(null)
    window.location.href = '/'
  } else if (status === 413) {
    showErrorToast('فایل آپلود شده بیشتر از حد مجاز است.')
  } else if (status >= 500) {
    showErrorToast('خطای سرور. لطفا بعداً دوباره تلاش کنید.')
  } else {
    const validationMessages = collectApiErrorMessages(
      data?.error,
      data?.errors
    )

    if (validationMessages.length > 0) {
      validationMessages.forEach((msg) => showErrorToast(msg))
    } else if (data?.message) {
      showErrorToast(data.message)
    } else if (status === 404) {
      showErrorToast('موردی یافت نشد!')
    } else if (status === 400) {
      showErrorToast('اطلاعات وارد شده معتبر نیست.')
    }
  }

  return Promise.reject(error)
}

const createAPIInstance = (
  contentType: string,
  requestInterceptor: (
    value: InternalAxiosRequestConfig
  ) => Promise<InternalAxiosRequestConfig>,
  responseInterceptor: (error: AxiosError<IResponseType<{}>>) => Promise<never>
): AxiosInstance => {
  const instance = axios.create({
    baseURL: BASE_URL,
    timeout: TIMEOUT,
    headers: {
      'Content-Type': contentType
    }
  })

  instance.interceptors.request.use(requestInterceptor, Promise.reject)
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    responseInterceptor
  )

  return instance
}

export const APIInstance = createAPIInstance(
  'application/json',
  requestInterceptor,
  responseInterceptor
)
export const APIFileInstance = createAPIInstance(
  'multipart/form-data',
  requestInterceptor,
  responseInterceptor
)
